import React from 'react';
import { chunk } from 'lodash';
import moment from 'moment-timezone';
import { Helmet } from 'react-helmet';

import CardLayout from '@magoosh/layouts/card';
import { DangerAlert } from '@magoosh/lib/alerts';
import { InfoButton, SecondaryButton } from '@magoosh/lib/button';
import useAuthenticityToken from '@magoosh/utils/use_authenticity_token';
import { paths } from 'config/path_helpers';
import { pluralize } from 'utilities/pluralize';
import { Branding } from 'types/branding';
import { Curriculum } from 'types/curriculum';
import { PaymentDetails } from 'types/payment_details';
import { PurchaseAgreement } from 'types/purchase_agreement';
import { User } from 'types/user';
import { ScoredExamSection, StudySchedule, UserProfile } from 'types/user_profile';

import ContextualLink from '@test_prep/app/components/contextual_link';
import { useAppContext } from '@test_prep/app/context';
import { useQueries } from '@test_prep/app/queries';

import Plan from './components/plan';

interface FieldProps {
  children: React.ReactNode;
  label: string;
}

interface PageProps {
  availableCurriculums: Curriculum[];
  branding: Branding;
  paymentMethods: PaymentDetails[];
  purchaseAgreements: PurchaseAgreement[];
  profile: UserProfile;
  stripeToken: string;
  studySchedules: StudySchedule[];
  user: User;
}

// A labelled value, half a row wide on anything but the narrowest screens
const Field: React.FC<FieldProps> = (props) => (
  <div className="flex-1">
    <h6 className="u-margin-A-n u-padding-B-xxs">{props.label}</h6>

    <div>{props.children}</div>
  </div>
);

// Scores read two to a row, the overall one first
const ScoreRows: React.FC<{ scores: { examSection: ScoredExamSection; value: number | null }[] }> = (props) => (
  <>
    {
      chunk(props.scores, 2).map((row, idx) => (
        <div key={idx} className="d-flex flex-column flex-sm-row gap-m">
          {
            row.map((score) => (
              <Field
                key={score.examSection.id}
                label={score.examSection.isRoot ? 'Overall' : score.examSection.name}
              >
                {score.value || <em>None</em>}
              </Field>
            ))
          }

          {row.length === 1 && <div className="flex-1" />}
        </div>
      ))
    }
  </>
);

const AccountSettings: React.FC<{}> = () => {
  const { branding } = useAppContext().data;
  const authenticityToken = useAuthenticityToken();
  const q = useQueries();

  const pageQuery = q.pageQuery(paths.profile());
  const page = pageQuery.data as PageProps;

  const breadcrumbs = [
    { name: 'Home', path: paths.dashboard() },
    { name: 'Account' }
  ];

  return (
    <>
      <Helmet>
        <title>Account - Magoosh {branding.examName}</title>
      </Helmet>

      <CardLayout breadcrumbs={breadcrumbs} linkComponent={ContextualLink} loading={!page}>
        {({ loading }) => {
          if (loading) return;

          const name = [page.user.firstName, page.user.lastName].filter(Boolean).join(' ');
          const hasOutstandingPayments = page.purchaseAgreements.some((purchaseAgreement) => (
            purchaseAgreement.outstandingPayments.length > 0
          ));

          return (
            <CardLayout.Card className="d-flex flex-column gap-l">
              {
                // The plans sit at the foot of the page, so a payment owed on one is flagged up here
                hasOutstandingPayments && (
                  <DangerAlert>
                    There's a problem with a payment on your plan.{' '}
                    <a
                      onClick={() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })}
                      style={{ color: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Review your plan
                    </a>{' '}
                    to keep your access.
                  </DangerAlert>
                )
              }

              <div className="d-flex flex-column flex-lg-row gap-l">
                <div className="d-flex flex-1 flex-column gap-l">
                  <div className="d-flex flex-column gap-m">
                    <div className="d-flex flex-column gap-s">
                      <h3 className="u-margin-A-n">Account</h3>
                      <hr className="u-margin-A-n" />
                    </div>

                    <div className="d-flex flex-column flex-sm-row gap-m">
                      <div className="d-flex flex-1 flex-column gap-m">
                        <Field label="Name">{name || <em>No name</em>}</Field>

                        <Field label="Phone Number">
                          {page.user.phoneNumber || <em>None</em>}
                        </Field>
                      </div>

                      <Field label="Email">{page.user.email}</Field>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-m">
                    <div className="d-flex flex-column gap-s">
                      <h3 className="u-margin-A-n">Profile</h3>
                      <hr className="u-margin-A-n" />
                    </div>

                    <div className="d-flex flex-column flex-sm-row gap-m">
                      <Field label="Test Date">
                        {
                          page.profile.testDate ? (
                            moment(page.profile.testDate).format('MMMM D, YYYY')
                          ) : (
                            <em>No test date</em>
                          )
                        }
                      </Field>

                      <Field label="Accommodation Settings">
                        {
                          page.profile.extraTimePercent ?
                            `${page.profile.extraTimePercent}% Extra Time` :
                            'None'
                        }
                      </Field>
                    </div>

                    <div className="d-flex flex-column flex-sm-row gap-m">
                      <Field label="Curriculum">{page.profile.curriculum.name}</Field>

                      <Field label="Study Schedule">
                        {page.profile.studySchedule?.name || 'No schedule selected'}
                      </Field>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-m">
                    <div className="d-flex flex-column gap-s">
                      <h3 className="u-margin-A-n">Target Score</h3>
                      <hr className="u-margin-A-n" />
                    </div>

                    <ScoreRows
                      scores={
                        page.profile.targetScores.map((score) => (
                          { examSection: score.examSection, value: score.target }
                        ))
                      }
                    />
                  </div>

                  <div className="d-flex flex-column gap-m">
                    <div className="d-flex flex-column gap-s">
                      <h3 className="u-margin-A-n">Previous Score</h3>
                      <hr className="u-margin-A-n" />
                    </div>

                    <ScoreRows
                      scores={
                        page.profile.actualScores.map((score) => (
                          { examSection: score.examSection, value: score.actual }
                        ))
                      }
                    />
                  </div>

                  <div className="d-flex flex-column gap-m" id="plans">
                    <div className="d-flex flex-column gap-s">
                      <h3 className="u-margin-A-n">{pluralize(page.purchaseAgreements.length, 'Plan')}</h3>
                      <hr className="u-margin-A-n" />
                    </div>

                    {
                      page.purchaseAgreements.map((purchaseAgreement, idx) => (
                        <React.Fragment
                          key={purchaseAgreement.id || `legacy-${purchaseAgreement.legacySubscriptionId}`}
                        >
                          {idx > 0 && <hr className="u-margin-A-n" />}

                          <Plan
                            onSuccess={() => pageQuery.refetch()}
                            paymentMethod={
                              page.paymentMethods.find((paymentMethod) => (
                                purchaseAgreement.id ?
                                  paymentMethod.purchaseAgreementId === purchaseAgreement.id :
                                  paymentMethod.legacySubscriptionId === purchaseAgreement.legacySubscriptionId
                              )) || null
                            }
                            purchaseAgreement={purchaseAgreement}
                            stripeToken={page.stripeToken}
                          />
                        </React.Fragment>
                      ))
                    }
                  </div>
                </div>

                <div className="d-flex flex-1 flex-column gap-l">
                  <div className="d-flex flex-column gap-m">
                    <div className="d-flex flex-column gap-s">
                      <h3 className="u-margin-A-n">Study Data</h3>
                      <hr className="u-margin-A-n" />
                    </div>

                    <div>
                      You can reset your progress through questions, lessons, study schedules, and
                      practice tests once each.
                    </div>

                    <div>
                      <SecondaryButton href={paths.reset()} outline size="sm">
                        Start Study Data Reset
                      </SecondaryButton>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-m">
                    <div className="d-flex flex-column gap-s">
                      <h3 className="u-margin-A-n">Referrals</h3>
                      <hr className="u-margin-A-n" />
                    </div>

                    <div>
                      Want to tell your friends about Magoosh and rewards for yourself? Click the
                      button below to learn more and to see the status of your referrals.
                    </div>

                    <div>
                      <InfoButton href={paths.referrals()} outline size="sm">
                        Refer a Friend
                      </InfoButton>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-m">
                    <div className="d-flex flex-column gap-s">
                      <h3 className="u-margin-A-n">Sessions</h3>
                      <hr className="u-margin-A-n" />
                    </div>

                    <div>
                      If you left your account logged in to a computer or phone you no longer have
                      access to, you can log that device out with this button.
                    </div>

                    <form action={paths.revokeSessions()} method="post">
                      <input type="hidden" name="authenticity_token" value={authenticityToken} />

                      <SecondaryButton outline size="sm" type="submit">
                        Log Out All Other Devices
                      </SecondaryButton>
                    </form>
                  </div>
                </div>
              </div>

              <div>
                <SecondaryButton href={paths.profileEdit()}>
                  Edit Account Settings
                </SecondaryButton>
              </div>
            </CardLayout.Card>
          );
        }}
      </CardLayout>
    </>
  );
};

export default AccountSettings;
